import { useEffect, useRef } from 'react'
import { IframeWithPlaceholder, IframeCaptureResult } from './types'

interface IframeCaptureProps {
  iframes: IframeWithPlaceholder[]
  onCaptureComplete: (captures: Record<string, IframeCaptureResult>) => void
  onProgress: (current: number, total: number) => void
}

const IframeCapture = ({ iframes, onCaptureComplete, onProgress }: IframeCaptureProps): null => {
  const hasStarted = useRef(false)
  // Store all props in refs to avoid dependency issues that cause effect re-runs
  const iframesRef = useRef(iframes)
  const onCaptureCompleteRef = useRef(onCaptureComplete)
  const onProgressRef = useRef(onProgress)

  // Update refs when props change (but don't trigger effect re-runs)
  useEffect(() => {
    iframesRef.current = iframes
    onCaptureCompleteRef.current = onCaptureComplete
    onProgressRef.current = onProgress
  }, [iframes, onCaptureComplete, onProgress])

  const findIframeInDOM = (src: string): HTMLIFrameElement | null => {
    const allIframes = document.querySelectorAll('iframe')

    for (const iframe of allIframes) {
      if (iframe.src === src || iframe.src.includes(src) || src.includes(iframe.src)) {
        return iframe
      }
    }

    return null
  }

  const captureRegion = async (stream: MediaStream, rect: DOMRect): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.srcObject = stream
      video.autoplay = true
      video.playsInline = true

      video.onloadedmetadata = async () => {
        try {
          await video.play()

          const videoWidth = video.videoWidth
          const videoHeight = video.videoHeight

          const scaleX = videoWidth / window.innerWidth
          const scaleY = videoHeight / window.innerHeight

          const sourceX = rect.left * scaleX
          const sourceY = rect.top * scaleY
          const sourceWidth = rect.width * scaleX
          const sourceHeight = rect.height * scaleY

          const canvas = document.createElement('canvas')
          canvas.width = rect.width * 2
          canvas.height = rect.height * 2

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }

          ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height)

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob'))
              }
            },
            'image/png',
            1.0
          )
        } catch (error) {
          reject(error)
        }
      }

      video.onerror = () => {
        reject(new Error('Video error during capture'))
      }
    })
  }

  const captureOneIframe = async (
    iframeElement: HTMLIFrameElement,
    stream: MediaStream,
    iframe: IframeWithPlaceholder,
    captures: Record<string, IframeCaptureResult>
  ) => {
    iframeElement.scrollIntoView({ behavior: 'auto', block: 'center' })
    // Wait for iframe content to load - external services like Miro may take longer
    await new Promise((resolve) => setTimeout(resolve, 3000))

    const rect = iframeElement.getBoundingClientRect()
    const blob = await captureRegion(stream, rect)

    captures[iframe.placeholderKey] = {
      blob,
      status: 'success',
      width: rect.width,
      height: rect.height
    }
  }

  // Start capture flow once when component mounts
  // Using empty dependency array - we use refs for all values to avoid re-runs
  useEffect(() => {
    const currentIframes = iframesRef.current

    if (currentIframes.length === 0) {
      return
    }

    if (hasStarted.current) {
      return
    }

    hasStarted.current = true

    const runCaptureFlow = async () => {
      const captures: Record<string, IframeCaptureResult> = {}
      let stream: MediaStream | null = null

      try {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
          // @ts-expect-error - Chrome-specific options not in TypeScript types
          selfBrowserSurface: 'include',
          preferCurrentTab: true
        })
      } catch {
        const iframesToCapture = iframesRef.current
        for (let i = 0; i < iframesToCapture.length; i++) {
          captures[iframesToCapture[i].placeholderKey] = {
            blob: new Blob(),
            status: 'failed',
            error:
              "Screen sharing wasn't allowed, so this iframe couldn't be captured. Select Retry and allow screen sharing when your browser asks."
          }
        }

        if (stream) {
          stream.getTracks().forEach((track) => track.stop())
        }
        onCaptureCompleteRef.current(captures)
        return
      }

      const iframesToCapture = iframesRef.current
      for (let i = 0; i < iframesToCapture.length; i++) {
        const iframe = iframesToCapture[i]
        onProgressRef.current(i, iframesToCapture.length)

        const iframeElement = findIframeInDOM(iframe.src)

        if (!iframeElement) {
          captures[iframe.placeholderKey] = {
            blob: new Blob(),
            status: 'failed',
            error: "This iframe isn't showing on the page. Expand the section that contains it, then select Retry."
          }
          continue
        }

        try {
          await captureOneIframe(iframeElement, stream, iframe, captures)
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "This iframe couldn't be captured. Select Retry to try again."
          captures[iframe.placeholderKey] = {
            blob: new Blob(),
            status: 'failed',
            error: message
          }
        }
      }

      onProgressRef.current(iframesToCapture.length, iframesToCapture.length)

      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }

      onCaptureCompleteRef.current(captures)
    }

    // Small delay to ensure UI has rendered
    const timer = setTimeout(runCaptureFlow, 500)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  return null
}

export default IframeCapture
